/**
 * useAiSummaryState — document state for AiSummaryPage: treatment selection,
 * AI notes, the editable summary HTML (+ version bump that re-seeds the
 * editor), generation meta and the revision chain (`editingReport`).
 *
 * Draft persistence: sessionStorage `clinikit_ai_summary_draft_v2_{clinic}_{patient}`
 * (12h TTL) so a refresh mid-work loses nothing. Free texts (title /
 * addressee / footer note) live in the shared export options object and are
 * drafted here too; layout toggles are NOT drafted (they come from prefs).
 * When the page is opened with `?reportId=` the draft is ignored and cleared
 * — the seeded report wins.
 */

import { useCallback, useEffect, useRef, useState } from "react";

const DRAFT_KEY = (clinicId, patientId) =>
  `clinikit_ai_summary_draft_v2_${clinicId}_${patientId}`;
const DRAFT_TTL_MS = 12 * 60 * 60 * 1000;

export const readDraft = (clinicId, patientId) => {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY(clinicId, patientId));
    if (!raw) return null;
    const draft = JSON.parse(raw);
    if (!draft || Date.now() - (draft.savedAt || 0) > DRAFT_TTL_MS) return null;
    return draft;
  } catch {
    return null;
  }
};

const writeDraft = (clinicId, patientId, draft) => {
  try {
    sessionStorage.setItem(
      DRAFT_KEY(clinicId, patientId),
      JSON.stringify({ ...draft, savedAt: Date.now() })
    );
  } catch {
    /* storage unavailable — drafts are a convenience only */
  }
};

export const clearDraft = (clinicId, patientId) => {
  try {
    sessionStorage.removeItem(DRAFT_KEY(clinicId, patientId));
  } catch {
    /* ignore */
  }
};

export const stripHtml = (html) => String(html || "").replace(/<[^>]*>/g, "").trim();

/**
 * @param {Object} a
 * @param {string} a.clinicId
 * @param {string} a.patientId
 * @param {boolean} a.seedFromReport  true when `?reportId=` is present (draft ignored)
 * @param {Object} a.options          shared export options (texts read/written here)
 * @param {Function} a.setOptions
 */
export function useAiSummaryState({ clinicId, patientId, seedFromReport, options, setOptions }) {
  const [selectedTreatmentIds, setSelectedTreatmentIds] = useState(() => new Set());
  const [aiNotes, setAiNotes] = useState("");
  const [summaryHtml, setSummaryHtml] = useState("");
  const [summaryMeta, setSummaryMeta] = useState(null); // { treatmentsUsed, truncated, dropped }
  const [summaryVersion, setSummaryVersion] = useState(0); // bump → editor re-seeds
  const [editingReport, setEditingReport] = useState(null); // { id, createdAt, revision }
  const [restoredNotice, setRestoredNotice] = useState(false);
  const [selectionTouched, setSelectionTouched] = useState(false);

  const applySummary = useCallback((html, meta) => {
    setSummaryHtml(html || "");
    setSummaryMeta(meta || null);
    setSummaryVersion((v) => v + 1);
  }, []);

  const setTexts = useCallback(
    (patch) => setOptions((prev) => ({ ...prev, ...patch })),
    [setOptions]
  );

  // ─── Draft restore (once per patient) ───
  const restoredForRef = useRef(null);
  useEffect(() => {
    if (!clinicId || !patientId || restoredForRef.current === patientId) return;
    restoredForRef.current = patientId;
    if (seedFromReport) {
      clearDraft(clinicId, patientId);
      return;
    }
    const draft = readDraft(clinicId, patientId);
    if (!draft) return;
    if (draft.aiNotes) setAiNotes(draft.aiNotes);
    const texts = {};
    if (draft.title) texts.title = draft.title;
    if (draft.addressee) texts.addressee = draft.addressee;
    if (draft.footerNote) texts.footerNote = draft.footerNote;
    if (Object.keys(texts).length) setTexts(texts);
    if (draft.editingReport?.id) setEditingReport(draft.editingReport);
    if (Array.isArray(draft.selectedTreatmentIds) && draft.selectedTreatmentIds.length) {
      setSelectedTreatmentIds(new Set(draft.selectedTreatmentIds));
      setSelectionTouched(true);
    }
    if (draft.summaryHtml) {
      applySummary(draft.summaryHtml, draft.summaryMeta || null);
      setRestoredNotice(true);
    }
  }, [clinicId, patientId, seedFromReport, setTexts, applySummary]);

  const summaryOk = stripHtml(summaryHtml).length > 0;

  // ─── Draft write ───
  useEffect(() => {
    if (!clinicId || !patientId) return;
    if (!summaryOk && !aiNotes) return;
    writeDraft(clinicId, patientId, {
      aiNotes,
      title: options.title,
      addressee: options.addressee,
      footerNote: options.footerNote,
      editingReport,
      summaryHtml,
      summaryMeta,
      selectedTreatmentIds: [...selectedTreatmentIds],
    });
  }, [
    clinicId,
    patientId,
    aiNotes,
    options.title,
    options.addressee,
    options.footerNote,
    editingReport,
    summaryHtml,
    summaryMeta,
    summaryOk,
    selectedTreatmentIds,
  ]);

  /** Seed from a previously exported report (history pencil). */
  const seedFromExportedReport = useCallback(
    (report) => {
      setSelectedTreatmentIds(
        new Set(Array.isArray(report.treatmentIds) ? report.treatmentIds : [])
      );
      setSelectionTouched(true);
      setAiNotes("");
      setTexts({
        title: report.title || "",
        addressee: report.subtitle || "",
        footerNote: report.footerNote || "",
      });
      setEditingReport({
        id: report.id,
        createdAt: report.createdAt || null,
        revision: Number(report.revision) || 1,
      });
      applySummary(report.contentHtml, null);
      setRestoredNotice(false);
    },
    [setTexts, applySummary]
  );

  const reset = useCallback(() => {
    clearDraft(clinicId, patientId);
    setSelectedTreatmentIds(new Set());
    setSelectionTouched(false);
    setAiNotes("");
    setSummaryMeta(null);
    setEditingReport(null);
    setRestoredNotice(false);
    setTexts({ title: "", addressee: "", footerNote: "" });
    applySummary("", null);
  }, [clinicId, patientId, setTexts, applySummary]);

  return {
    selectedTreatmentIds,
    setSelectedTreatmentIds: (next) => {
      setSelectionTouched(true);
      setSelectedTreatmentIds(next);
    },
    selectionTouched,
    defaultSelection: (ids) => {
      // Only when nothing (draft / seed / user) picked yet.
      if (!selectionTouched) setSelectedTreatmentIds(new Set(ids));
    },
    aiNotes,
    setAiNotes,
    summaryHtml,
    setSummaryHtml,
    summaryMeta,
    summaryVersion,
    summaryOk,
    applySummary,
    editingReport,
    setEditingReport,
    restoredNotice,
    seedFromExportedReport,
    reset,
    clearDraft: () => clearDraft(clinicId, patientId),
  };
}

export default useAiSummaryState;
